import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { withPrefix, graphql } from 'gatsby';

import {
    Container,
    Text,
    isMobile,
    Col,
    Row,
    Heading,
    Card,
} from 'design-system';
import { useTranslate } from 'gatsby-theme-soluble-source';
import { GatsbyImage } from 'gatsby-plugin-image';

import ButtonComponent from '../ButtonComponent';
import TransitionLink from '../TransitionLink';

import style from './styles.module.scss';

const FIRST_PAGE_SIZE = isMobile() ? 3 : 5;
const PAGE_SIZE = 4;

function getItemNumbers(page, pageSize, firstPageSize) {
    if (!firstPageSize) {
        firstPageSize = pageSize;
    }

    return page
        ? pageSize
            ? firstPageSize + (page - 1) * pageSize
            : FIRST_PAGE_SIZE + (page - 1) * PAGE_SIZE
        : 0;
}

let y = 0;

function ArticleItemCard({ isFirst = false, title, slug, servicesSectors }) {
    const translation = useTranslate();

    const services = useMemo(() => {
        if (!servicesSectors?.length) {
            return null;
        }

        return servicesSectors
            .filter((ss) => !!ss.title && !ss?.hide)
            .map((ss) => ss.title)
            .join(', ');
    }, [servicesSectors]);

    return (
        <Card
            wrapperClassName={`${
                style[`card${isFirst ? '--first' : ''}`]
            } shadow`}
            bodyClassName={style['card__body']}
            title={title}
            titleHref={`${withPrefix(slug)}/`}
            preTitleComponent={
                services ? (
                    <Text
                        className={`font-weight-bold subtitle text-uppercase mb-3 d-block ${style['card__pretitle']}`}>
                        {services}
                    </Text>
                ) : null
            }
            titleClassName={`h5 font-weight-bold ${style['card__title']}`}>
            <ButtonComponent
                buttonClassName={style['card__button']}
                button={{
                    type: 'secondary',
                    label: translation['articles-section.article.cta'].text,
                    link: { slug },
                }}
            />
        </Card>
    );
}

export default function ArticlesSection({
    pretitle,
    title,
    posts,
    pt,
    pb,
    firstPageSize,
    pageSize,
    previousSectionType,
}) {
    const translation = useTranslate();

    const [page, setPage] = useState(1);
    const [currentPost, setCurrentPost] = useState(
        posts
            ? posts.slice(0, getItemNumbers(page, pageSize, firstPageSize))
            : [],
    );
    const [hasMore, setHasMore] = useState(true);
    const loadMore = useCallback(() => {
        y = window.scrollY;

        setCurrentPost(
            posts
                ? posts.slice(
                      0,
                      getItemNumbers(page + 1, pageSize, firstPageSize),
                  )
                : [],
        );
        setPage(page + 1);
    }, [setCurrentPost, setPage, page, posts, pageSize, firstPageSize]);

    useEffect(() => {
        if (page > 1) {
            window.scrollTo(window.scrollX ? window.scrollX : 0, y);
        }

        if (
            !posts?.length ||
            getItemNumbers(page, pageSize, firstPageSize) >= posts.length
        ) {
            setHasMore(false);
        }
    }, [page, setHasMore, posts, pageSize, firstPageSize]);

    const finalPt = useMemo(() => {
        if (pt) {
            return pt;
        }

        if (
            previousSectionType &&
            previousSectionType === 'DatoCmsCaseStudiesSection'
        ) {
            return { xs: 10, lg: 13 };
        }

        return {};
    }, [previousSectionType, pt]);

    return (
        <section data-datocms-noindex>
            <Container pt={finalPt} pb={pb ? pb : { xs: 20, lg: 30 }}>
                <Row>
                    {title || pretitle ? (
                        <Col className="text-center">
                            {pretitle ? (
                                <Text
                                    tag="p"
                                    className={`d-block mb-3 subtitle text-uppercase font-weight-bold ${style.pretitle}`}>
                                    {pretitle}
                                </Text>
                            ) : null}
                            {title ? (
                                <Heading
                                    className="h1"
                                    tag="h3"
                                    mb={{ xs: 10 }}>
                                    {title}
                                </Heading>
                            ) : null}
                        </Col>
                    ) : null}
                    {currentPost.map((post, i) => {
                        return (
                            <Col
                                key={i}
                                pb={{ xs: 5 }}
                                col={{ lg: !i ? 10 : 5 }}
                                offset={{ lg: !i || i % 2 === 1 ? 1 : 0 }}>
                                {!i && post.featuredImage?.gatsbyImageData ? (
                                    <TransitionLink
                                        to={post.slug}
                                        title={post.title}
                                        className={style['image-wrapper']}>
                                        <GatsbyImage
                                            alt={post.featuredImage?.alt || ''}
                                            image={
                                                post.featuredImage
                                                    .gatsbyImageData
                                            }
                                        />
                                    </TransitionLink>
                                ) : null}
                                <ArticleItemCard isFirst={!i} {...post} />
                            </Col>
                        );
                    })}
                    {hasMore ? (
                        <Col className="text-center" pt={{ lg: 5 }}>
                            <ButtonComponent
                                button={{
                                    type: 'primary-xl',
                                    Tag: 'button',
                                    label: translation['articles-section.more']
                                        .text,
                                    onClick: loadMore,
                                }}
                            />
                        </Col>
                    ) : null}
                </Row>
            </Container>
        </section>
    );
}

export const query = graphql`
    # prettier-ignore
    fragment ArticleFeaturedImage on DatoCmsFileField {
        alt
        gatsbyImageData(
            placeholder: BLURRED
            layout: FIXED
            imgixParams: { w: "960", h: "900", fit: "crop"}
        )
    }
    fragment ArticleItem on DatoCmsArticle {
        slug
        title
        featuredImage {
            ...ArticleFeaturedImage
        }
        servicesSectors {
            ... on DatoCmsService {
                ...DatoCmsService
            }
        }
    }

    fragment DatoCmsArticlesSection on DatoCmsArticlesSection {
        pretitle
        title
        posts {
            ...ArticleItem
        }
    }
`;
